import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  BookOpen, ChevronRight, ChevronDown, Search,
  ShoppingCart, Package, FileText, Users, BarChart3, Truck,
  Shield, Printer, Smartphone, Settings, CreditCard, Boxes,
  Camera, ScanLine, Megaphone, Globe, Lock, HelpCircle
} from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import ShopLayout from '@/components/shop/ShopLayout';

const DOCS = [
  {
    id: 'getting-started',
    icon: BookOpen,
    title: 'Getting Started',
    sections: [
      {
        title: 'Creating Your Account',
        content: `1. Go to vendfactura.es and click "Get Started"\n2. Enter your name, email, and a strong password\n3. Verify your email address\n4. You'll get a 14-day free trial with full access to all features\n\nNo credit card is required to start your trial.`,
      },
      {
        title: 'Setting Up Your Shop',
        content: `After logging in for the first time:\n\n1. Go to **Settings** in the sidebar\n2. Fill in your shop details:\n   - Shop name and description\n   - Address, city, and postal code\n   - Phone number and email\n   - Opening hours for each day of the week\n3. Choose your currency (EUR or GBP)\n4. Click **Save Settings**\n\nYour shop is now ready to use!`,
      },
      {
        title: 'Adding Your First Products',
        content: `There are 3 ways to add products:\n\n**Option 1: Browse Global Catalog**\n- Go to Products page\n- Click "Browse Catalog"\n- Search or filter by category\n- Select products and click "Add to Shop"\n- Over 100 pre-loaded products with barcodes\n\n**Option 2: Add Manually**\n- Click "New Product"\n- Enter name, price, category, barcode, stock count\n- Upload an image or leave blank\n- Click Save\n\n**Option 3: Bulk Import via CSV**\n- Click the upload icon on the Products page\n- Upload a CSV file with columns: name, price, category, barcode, stock_count\n- All products are created at once`,
      },
    ],
  },
  {
    id: 'pos',
    icon: ShoppingCart,
    title: 'Point of Sale (POS)',
    sections: [
      {
        title: 'Making a Sale',
        content: `1. Go to **POS** in the sidebar\n2. Browse products by category or search by name\n3. Click a product to add it to the cart\n4. Click again to add more (or use +/- buttons in the cart)\n5. Click **Pay** when ready\n6. Choose payment method (Cash or Card)\n7. For cash: enter amount received or click "Exact"\n8. Click **Complete Sale**\n9. The receipt shows with VeriFactu compliance data\n\n**Tips:**\n- Use a USB barcode scanner for faster product lookup\n- Use the camera scan button on mobile/tablet\n- The POS works offline — sales sync when connection returns`,
      },
      {
        title: 'POS Staff Lock',
        content: `For shared terminals with multiple cashiers:\n\n1. Go to **Settings** > POS Staff Lock\n2. Toggle "Enable POS Lock" on\n3. Set auto-lock timer (1, 3, 5, 10, or 15 minutes)\n4. Each staff member sets their own 4-digit PIN in Staff Management\n\nWhen locked, staff enter their PIN to access the POS. This tracks who made each sale.`,
      },
    ],
  },
  {
    id: 'products',
    icon: Package,
    title: 'Products & Catalog',
    sections: [
      {
        title: 'Managing Products',
        content: `The Products page shows all your shop's products in a table.\n\n**Edit a product:** Click the pencil icon\n**Delete a product:** Click the trash icon\n**Print barcode:** Click the printer icon on any product\n\n**Bulk actions:** Use checkboxes to select multiple products, then:\n- "Print Barcodes" — Print label sheets for selected products\n- "Delete" — Remove selected products`,
      },
      {
        title: 'Barcode Scanning',
        content: `VendFactura supports two types of barcode scanning:\n\n**USB Scanner:** Simply plug in a USB barcode scanner. Scan a barcode on the Products or POS page — if the product exists, it's found instantly. If not, a new product form opens with the barcode pre-filled.\n\n**Camera Scanner:** Click the "Camera" button on Products, POS, or Stock pages. Point your phone/tablet camera at a barcode. Works with EAN-13, EAN-8, UPC-A, and Code-128 barcodes.`,
      },
      {
        title: 'Printing Barcode Labels',
        content: `**Single product:**\n1. Click the printer icon on any product\n2. Enter or generate a barcode\n3. Choose label size (Small, Medium, Large)\n4. Set number of copies\n5. Toggle "Show price" on/off\n6. Click "Print Label"\n\n**Bulk printing:**\n1. Select multiple products with checkboxes\n2. Click "Print Barcodes"\n3. Choose size, copies per product\n4. Click "Print" — all labels on one sheet\n\nUse a thermal label printer for best results.`,
      },
    ],
  },
  {
    id: 'stock',
    icon: Boxes,
    title: 'Stock Management',
    sections: [
      {
        title: 'Stock Overview',
        content: `The Stock page shows:\n- **Total products** tracked\n- **Low stock alerts** (items below threshold)\n- **Out of stock** count\n\nThe table shows each product's current stock, cost price, sale price, and status. You can:\n- **Inline edit:** Click the pencil icon to edit quantity, cost, and price directly\n- **Bulk edit:** Select multiple products → click "Bulk Edit" → set new values for all\n- **Add stock:** Click + to record a stock intake\n- **Adjust stock:** Click the arrows icon for corrections`,
      },
      {
        title: 'AI Receipt Scanner',
        content: `Automatically add stock from a supplier receipt or delivery note:\n\n1. Go to Stock > AI Receipt Scanner tab\n2. Click "Upload Receipt" and select a photo of your receipt\n3. The AI extracts product names, quantities, and costs\n4. **Matched items** are linked to existing products — click "Add Matched Items to Stock"\n5. **Unmatched items** are new — you can edit the name, quantity, cost, and unit before clicking "Create & Add as New Products"\n\n**Tips:**\n- Take clear, well-lit photos for best results\n- The AI reads pack quantities from descriptions (e.g. "4 packs of 2")\n- You can edit all extracted values before confirming`,
      },
      {
        title: 'Exporting Stock Data',
        content: `Export your stock data for accounting or analysis:\n\n- **CSV Export:** Click "Export CSV" — opens a spreadsheet-compatible file\n- **PDF Export:** Click "Export PDF" — generates a formatted report\n\nWhen products are selected, only those products are exported. Otherwise, all products are included.`,
      },
    ],
  },
  {
    id: 'invoices',
    icon: FileText,
    title: 'Invoicing',
    sections: [
      {
        title: 'Creating Invoices',
        content: `VendFactura creates VeriFactu-compliant invoices automatically with every sale.\n\nFor manual invoices:\n1. Go to Invoices page\n2. Click "New Invoice"\n3. Select or create a client\n4. Add line items with descriptions, quantities, and prices\n5. The system auto-calculates IVA (21%, 10%, 4%, or 0%)\n6. Click "Create Invoice"\n\nEvery invoice gets a unique fiscal number, cryptographic hash, and QR code.`,
      },
      {
        title: 'Sending Invoices',
        content: `Invoices can be:\n- **Downloaded as PDF** — Click the download icon\n- **Emailed to client** — Click the email icon (requires client email)\n- **Printed** — Click the print icon\n\nVeriFactu compliance data (hash, QR code, IVA breakdown) is included automatically on all invoices and receipts.`,
      },
    ],
  },
  {
    id: 'verifactu',
    icon: Shield,
    title: 'VeriFactu Compliance',
    sections: [
      {
        title: 'What is VeriFactu?',
        content: `VeriFactu is Spain's anti-fraud regulation that requires ALL businesses using invoicing software to:\n\n1. **Invoice integrity** — Invoices cannot be deleted or manipulated\n2. **Traceability** — Complete audit trail via cryptographic hash chain\n3. **QR codes** — Every invoice must have a verification QR code\n4. **AEAT reporting** — Automatic submission to the Tax Authority\n\nThis applies to: Companies (SL, SA), Freelancers (Autonomos), Shops, Restaurants, and all businesses.`,
      },
      {
        title: 'Setting Up VeriFactu',
        content: `1. Go to **VeriFactu** in the sidebar\n2. Click "Settings"\n3. Enter your fiscal data:\n   - NIF (tax ID number)\n   - Razon Social (legal business name)\n   - Invoice series prefix (e.g. "A-")\n4. Click Save\n\n**Important:** Enter your data exactly as registered with AEAT to avoid compliance issues.\n\nThe VeriFactu dashboard shows:\n- Total fiscal invoices generated\n- Hash chain integrity status\n- AEAT submission success rate`,
      },
    ],
  },
  {
    id: 'staff',
    icon: Users,
    title: 'Staff Management',
    sections: [
      {
        title: 'Adding Staff Members',
        content: `1. Go to **Staff** in the sidebar\n2. Click "Add Staff"\n3. Enter their name, email, and role:\n   - **Manager** — Full access to shop\n   - **Cashier** — POS and basic product access\n   - **Stock Clerk** — Products and stock management\n   - **Driver** — Delivery management\n4. Set a 4-digit POS PIN for the staff member\n5. Click Save\n\nEach role only sees the pages they need. Managers see everything.`,
      },
    ],
  },
  {
    id: 'reports',
    icon: BarChart3,
    title: 'Reports & Analytics',
    sections: [
      {
        title: 'Daily Reports',
        content: `Go to **Reports** in the sidebar to see:\n\n- **Revenue** — Total sales for the period\n- **Orders** — Number of completed sales\n- **Average sale** — Revenue divided by orders\n- **Hourly breakdown** — Bar chart of sales by hour\n- **Top products** — Best-selling items\n\n**Time periods:**\n- Today, Yesterday, This Week, Last Week\n- This Month, Last Month, This Year\n- Custom date range\n\n**Export:** Click "Export CSV" to download report data for your accountant.`,
      },
    ],
  },
  {
    id: 'delivery',
    icon: Truck,
    title: 'Delivery Management',
    sections: [
      {
        title: 'Setting Up Delivery',
        content: `1. Go to **Settings** > Service Availability\n2. Toggle "Delivery" on\n3. Set delivery radius (km), minimum order (EUR), and delivery fee\n4. Go to **Staff** and add drivers\n\n**Managing orders:**\n- Delivery orders appear on the Delivery page\n- Assign orders to available drivers\n- Track status: Pending → Assigned → Picked Up → Delivered`,
      },
    ],
  },
  {
    id: 'social',
    icon: Megaphone,
    title: 'Social Media Posts',
    sections: [
      {
        title: 'Generating Promotional Posts',
        content: `Create eye-catching social media content when new stock arrives:\n\n1. Go to **Social Posts** in the sidebar\n2. Click "Generate New Post"\n3. Select the products you want to feature (up to 10)\n4. Click "Generate Post" — AI creates a promotional image and caption\n5. Generation takes up to 60 seconds\n\nThe result includes:\n- **Promotional image** (1080x1080, perfect for Instagram)\n- **Caption** for Facebook/Instagram\n- **Short caption** for Twitter/X\n- **Hashtags** relevant to your products and location`,
      },
      {
        title: 'Connecting Facebook + Instagram for Auto-Publish',
        content: `**Step 1 — Create a Facebook Page** (skip if you already have one)\n1. Go to facebook.com/pages/create\n2. Pick a category and add your shop name\n3. Add a profile picture and a cover image\n\n**Step 2 — Connect Instagram Business**\n1. Convert your Instagram to a Business account: Profile → Settings → Account type → Switch to Professional → Business\n2. Open Meta Business Suite (business.facebook.com)\n3. Settings → Linked Accounts → Connect your Instagram\n\n**Step 3 — Get a Page Access Token**\n1. Open the Graph API Explorer: developers.facebook.com/tools/explorer\n2. Log in with your Facebook account (the one that owns the Page)\n3. Click "Get Token" → "Get Page Access Token"\n4. Select your Page\n5. Click "Add a Permission" and grant:\n   - pages_show_list\n   - pages_manage_posts\n   - pages_read_engagement\n   - instagram_basic\n   - instagram_content_publish\n6. Click "Generate Access Token"\n\n**Step 4 — Extend the token to long-lived (60 days)**\n1. Copy the short-lived token\n2. Open the Access Token Debugger: developers.facebook.com/tools/debug/accesstoken\n3. Paste the token, click Debug\n4. Click "Extend Access Token" at the bottom\n5. Copy the new long-lived token\n\n**Step 5 — Get your Page ID**\n1. Open your Facebook Page\n2. Click "About"\n3. Scroll down to find "Page ID" (15+ digit number)\n\n**Step 6 — Connect in VendFactura**\n1. Go to Social Posts page\n2. Click "Connect Facebook"\n3. Paste Page ID and the long-lived token\n4. Click "Validate & Connect"\n5. VendFactura confirms the page name + linked Instagram account\n\nFrom now on, every generated post can be published with one click to Facebook and Instagram simultaneously.\n\n**Cost: FREE.** Meta does not charge for posting through the Graph API.\n\n**Token lifetime: 60 days.** When it expires, regenerate from Graph API Explorer and reconnect. We'll show you a banner when a refresh is needed.`,
      },
      {
        title: 'Publishing a Post to Facebook + Instagram',
        content: `Once your accounts are connected:\n\n1. Generate a post on the Social Posts page (or open an existing draft)\n2. Click "Publish" in the post viewer\n3. Choose platforms: Facebook, Instagram, or both\n4. Click "Publish Now"\n\nResults appear inside the modal:\n- Green ✓ with a clickable link to the live post\n- Red × with the specific error and how to fix it\n\nCommon errors:\n- **Invalid OAuth access token** — Your 60-day token expired. Regenerate and reconnect.\n- **Permission missing** — Re-generate token with all 5 permissions checked.\n- **Instagram not linked** — Connect your IG Business account to the Facebook Page in Meta Business Suite first.\n- **Image not accessible** — Rare, usually a temporary Meta-side issue; retry in a minute.`,
      },
    ],
  },
  {
    id: 'subscription',
    icon: CreditCard,
    title: 'Subscription & Billing',
    sections: [
      {
        title: 'Plans & Pricing',
        content: `VendFactura offers two plans:\n\n**Invoice Creator — EUR 4.99/month**\n- VeriFactu-compliant invoicing\n- Client management\n- PDF generation and email\n- Reports\n\n**Shop POS — EUR 19.99/month**\n- Full POS system\n- Stock management\n- Staff and delivery\n- VeriFactu compliance\n- All features included\n\n**Yearly billing:** Save 17% by paying annually.\n\n**Free trial:** 14 days with full access, no credit card required.`,
      },
      {
        title: 'After Your Trial',
        content: `When your 14-day trial ends:\n\n1. **Grace period (30 days):** A banner reminds you to subscribe. All features still work.\n2. **Read-only mode (3 months):** You can view your data but cannot create new sales or invoices.\n3. **Account locked:** After 3 months, log in to subscribe and regain access.\n\nTo subscribe: Go to **Subscription** in the sidebar and choose your plan.`,
      },
    ],
  },
  {
    id: 'mobile',
    icon: Smartphone,
    title: 'Mobile App (PWA)',
    sections: [
      {
        title: 'Installing on Your Phone',
        content: `VendFactura is a Progressive Web App (PWA) — install it like a native app:\n\n**Android (Chrome):**\n1. Open vendfactura.es in Chrome\n2. Log in to your account\n3. Tap the three-dot menu (top right)\n4. Tap "Add to Home Screen" or "Install App"\n5. Tap "Install"\n\n**iPhone (Safari):**\n1. Open vendfactura.es in Safari\n2. Log in to your account\n3. Tap the Share button (bottom center)\n4. Scroll down and tap "Add to Home Screen"\n5. Tap "Add"\n\nThe app works offline and syncs when connected.`,
      },
    ],
  },
  {
    id: 'printer',
    icon: Printer,
    title: 'Printer Setup',
    sections: [
      {
        title: 'Connecting a Receipt Printer',
        content: `VendFactura supports ESC/POS thermal receipt printers.\n\n**USB connection:**\n1. Plug your thermal printer into the tablet/computer via USB\n2. The browser should detect it automatically\n3. Go to Settings > Printer and test the connection\n\n**Network printer:**\n1. Connect the printer to your WiFi/LAN network\n2. Note the printer's IP address\n3. Enter the IP in Settings > Printer\n\n**Supported printers:** Epson TM series, Star Micronics, and any ESC/POS compatible printer.\n\n**Barcode label printers:** Any standard label printer works with the barcode printing feature. Use the medium (50x30mm) label size for most common label printers.`,
      },
    ],
  },
  {
    id: 'settings',
    icon: Settings,
    title: 'Shop Settings',
    sections: [
      {
        title: 'All Settings',
        content: `Go to **Settings** in the sidebar to configure:\n\n- **Basic Information:** Shop name, description, image\n- **Contact & Location:** Address, city, postal code, phone, email\n- **Service Availability:** Toggle delivery and takeaway on/off\n- **Delivery Settings:** Radius, minimum order, delivery fee\n- **Currency:** EUR or GBP\n- **Report Emails:** Email addresses for end-of-day reports\n- **POS Staff Lock:** Enable/disable, auto-lock timer\n- **Admin POS PIN:** Your 4-digit PIN for POS access\n- **Opening Hours:** Set times for each day of the week`,
      },
    ],
  },
  {
    id: 'language',
    icon: Globe,
    title: 'Language',
    sections: [
      {
        title: 'Changing Language',
        content: `VendFactura supports 7 languages:\n- Spanish, English, French, German, Dutch, Hindi, Chinese\n\nTo change the language in the dashboard:\n1. Click the globe icon in the top header bar\n2. Select your preferred language\n3. The change applies instantly across all pages\n\nThe landing page language can also be changed from the top navigation bar.`,
      },
    ],
  },
];

const DocsPage = () => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedDoc, setExpandedDoc] = useState('getting-started');
  const [expandedSection, setExpandedSection] = useState(0);

  const filteredDocs = searchQuery
    ? DOCS.filter(d =>
        d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.sections.some(s =>
          s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.content.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )
    : DOCS;

  return (
    <ShopLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="docs-page">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Help & Documentation</h1>
          <p className="text-slate-500 text-sm">Everything you need to know about VendFactura</p>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search documentation..."
            className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-sm text-sm focus:border-[#1e3a5f] focus:outline-none"
            data-testid="docs-search" />
        </div>

        {/* Docs Accordion */}
        <div className="space-y-2">
          {filteredDocs.map(doc => {
            const Icon = doc.icon;
            const isExpanded = expandedDoc === doc.id;
            return (
              <div key={doc.id} className="bg-white border-2 border-slate-100 overflow-hidden" data-testid={`doc-${doc.id}`}>
                <button onClick={() => { setExpandedDoc(isExpanded ? null : doc.id); setExpandedSection(0); }}
                  className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition-colors">
                  <div className="w-9 h-9 bg-[#1e3a5f] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-base font-semibold text-[#1e3a5f] flex-1">{doc.title}</span>
                  <span className="text-xs text-slate-400">{doc.sections.length} {doc.sections.length === 1 ? 'article' : 'articles'}</span>
                  {isExpanded ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
                </button>

                {isExpanded && (
                  <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} className="border-t border-slate-100">
                    {doc.sections.map((section, si) => (
                      <div key={si}>
                        <button onClick={() => setExpandedSection(expandedSection === si ? -1 : si)}
                          className={`w-full flex items-center gap-2 px-5 py-3 text-left text-sm border-b border-slate-50 transition-colors ${
                            expandedSection === si ? 'bg-green-50 text-[#22c55e] font-medium' : 'text-slate-700 hover:bg-slate-50'
                          }`}>
                          {expandedSection === si ? <ChevronDown className="w-4 h-4 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                          {section.title}
                        </button>
                        {expandedSection === si && (
                          <div className="px-5 py-4 bg-slate-50/50 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                            {section.content.split('**').map((part, pi) =>
                              pi % 2 === 1 ? <strong key={pi} className="text-[#1e3a5f]">{part}</strong> : part
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </ShopLayout>
  );
};

export default DocsPage;
